<template>
  <nav class="language-switcher" :aria-label="t('common.languages.label')">
    <button
      v-for="language in languages"
      :key="language"
      type="button"
      class="language-switcher__option"
      :class="{ 'language-switcher__option--active': locale === language }"
      :aria-current="locale === language ? 'true' : undefined"
      :aria-label="languageLabel(language)"
      :title="t(`common.languages.${language}`)"
      @click="selectLanguage(language)"
    >
      {{ language.toUpperCase() }}
    </button>
  </nav>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import { setLocale, supportedLocales } from '@/i18n';

const languages = supportedLocales;
const { locale, t } = useI18n({ useScope: 'global' });

function languageLabel(language) {
  const name = t(`common.languages.${language}`);
  return locale.value === language
    ? t('common.languages.current', { language: name })
    : name;
}

function selectLanguage(language) {
  if (locale.value !== language) setLocale(language);
}
</script>

<style scoped>
.language-switcher {
  display: inline-flex;
  align-items: stretch;
  align-self: stretch;
  gap: 0.15rem;
  min-height: 2.75rem;
}

.language-switcher__option {
  position: relative;
  min-width: 2.15rem;
  min-height: 2.75rem;
  margin: 0;
  padding: 0.15rem 0.35rem;
  border: 0;
  color: rgba(255, 255, 255, 0.78);
  background: transparent;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: color 160ms ease, background-color 160ms ease;
}

.language-switcher__option::after {
  position: absolute;
  right: 0.35rem;
  bottom: 0;
  left: 0.35rem;
  height: 0.18rem;
  background: var(--color-gold);
  content: '';
  opacity: 0;
  transform: scaleX(0.4);
  transition: opacity 160ms ease, transform 160ms ease;
}

.language-switcher__option:hover {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.08);
}

.language-switcher__option--active {
  color: #ffffff;
}

.language-switcher__option--active::after {
  opacity: 1;
  transform: scaleX(1);
}

.language-switcher__option:focus-visible {
  z-index: 1;
}

@media (max-width: 30rem) {
  .language-switcher {
    gap: 0;
  }

  .language-switcher__option {
    min-width: 2rem;
    padding-inline: 0.25rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .language-switcher__option,
  .language-switcher__option::after {
    transition: none;
  }
}
</style>
